// ... existing imports ...
import { useEffect, useState, useRef, useMemo } from "react";
import Header from "../components/Header";
import { FilterPanel } from "../components/revenue/FilterPanel";
import { RevenueTable } from "../components/revenue/RevenueTable";
import { STATUS_ORDER } from "../components/revenue/constants";
import { RevenueGraph } from '../components/revenue/RevenueGraph';
import { useData } from "../context/DataContext";
import { useCacheCheck } from "../hooks/useCacheCheck";
import { CacheStatus } from "../components/CacheStatus";
import { DataCheckModal } from "../components/DataCheckModal";
import { UserManualModal } from "../components/UserManualModal";
import { ExportButton } from "../components/ExportButton";
// Add TypeScript declaration for window object
declare global {
  interface Window {
    allTeamMembers?: any[];
  }
}

// Add this helper function at the top with other helpers
function formatCurrency(amount: number): string {
  const formattedNumber = Math.round(amount)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formattedNumber}€`;
}

// Define column widths to match RevenueTable
const managerColumnWidth = '5.5rem';
const nbProjectsWidth = '5.5rem';
const groupsColumnsWidth = 'minmax(40px, 1fr)';
const otherColumnsWidth = 'minmax(60px, 1fr)';
const realisedRevenueWidth = '6.5rem';
const materializationWidth = '5.5rem';

// Add this new component for the total row
function TotalRow({
  rows,
  statuses,
  gridStyle
}: {
  rows: any[],
  statuses: string[],
  gridStyle: any
}) {
  // Calculate totals
  const totals = rows.reduce((acc, row) => {
    statuses.forEach(status => {
      if (!acc[status]) {
        acc[status] = { nb: 0, est: 0, real: 0 };
      }
      acc[status].nb += (row[status]?.nb || 0);
      acc[status].est += (row[status]?.est || 0);
      acc[status].real += (row[status]?.real || 0);
    });
    acc.totalGroups = (acc.totalGroups || 0) + row.totalGroups;
    acc.totalEst = (acc.totalEst || 0) + row.totalEst;
    acc.totalReal = (acc.totalReal || 0) + row.totalReal;
    acc.totalProjects = (acc.totalProjects || 0) + (row.nbProjects || 0);
    return acc;
  }, {});

  return (
    <div
      className="gap-2 px-3 py-1.5 text-xs text-gray-700 grid border-t-2 border-gray-200 bg-gray-50 font-bold"
      style={gridStyle}
    >
      <div className="font-bold flex items-center text-sm">TOTAL</div>
      <div className="text-center">{totals.totalProjects}</div>
      {statuses.map(status => (
        <div key={"nb-"+status} className="text-center">{totals[status]?.nb || 0}</div>
      ))}
      <div className="text-center">{totals.totalGroups}</div>
      {statuses.map(status => (
        <div key={"est-"+status} className="text-center">
          {formatCurrency(totals[status]?.est || 0)}
        </div>
      ))}
      <div className="text-center">
        {formatCurrency(totals.totalEst)}
      </div>
      <div className="text-center">
        {formatCurrency(totals.totalReal)}
      </div>
      <div className="text-center">
        {totals.Booked?.est > 0 ? Math.round((totals.totalReal / totals.Booked.est) * 100) + '%' : '-'}
      </div>
    </div>
  );
}

// --- Main Component ---
export default function EstimatedRevenuePerManager() {
  const { data, loading: isLoading, error, loadingProgress } = useData();
  
  // Check cache when component mounts
  useCacheCheck();
  
  const [expandedManagers, setExpandedManagers] = useState<Set<string>>(new Set());

  // Available options (derived from data)
  const [availableCreationYears, setAvailableCreationYears] = useState<number[]>([]);
  const [availableArrivalYears, setAvailableArrivalYears] = useState<number[]>([]);
  const [availableManagers, setAvailableManagers] = useState<string[]>([]);
  const [availableSegments, setAvailableSegments] = useState<string[]>([]);
  const [clientOptions, setClientOptions] = useState<string[]>([]);
  const [hotels, setHotels] = useState<string[]>([]);
  const [hotelGroups, setHotelGroups] = useState<string[]>([]);
  const [markets, setMarkets] = useState<string[]>([]);

  // Applied state (what's currently filtering the data)
  const [appliedCreationYears, setAppliedCreationYears] = useState<Set<number>>(new Set());
  const [appliedArrivalYears, setAppliedArrivalYears] = useState<Set<number>>(new Set());
  const [appliedManagers, setAppliedManagers] = useState<Set<string>>(new Set());
  const [appliedSegments, setAppliedSegments] = useState<Set<string>>(new Set());
  const [appliedClients, setAppliedClients] = useState<Set<string>>(new Set());
  const [appliedHotels, setAppliedHotels] = useState<Set<string>>(new Set());
  const [appliedHotelGroups, setAppliedHotelGroups] = useState<Set<string>>(new Set());
  const [appliedArrivalMonths, setAppliedArrivalMonths] = useState<Set<number>>(new Set());
  const [appliedArrivalQuarters, setAppliedArrivalQuarters] = useState<Set<number>>(new Set());
  const [appliedCreationMonths, setAppliedCreationMonths] = useState<Set<number>>(new Set());
  const [appliedCreationQuarters, setAppliedCreationQuarters] = useState<Set<number>>(new Set());
  const [appliedMarkets, setAppliedMarkets] = useState<Set<string>>(new Set());

  // Pending state (what user is selecting in UI - not yet applied)
  const [pendingCreationYears, setPendingCreationYears] = useState<Set<number>>(new Set());
  const [pendingArrivalYears, setPendingArrivalYears] = useState<Set<number>>(new Set());
  const [pendingManagers, setPendingManagers] = useState<Set<string>>(new Set());
  const [pendingSegments, setPendingSegments] = useState<Set<string>>(new Set());
  const [pendingClients, setPendingClients] = useState<Set<string>>(new Set());
  const [pendingHotels, setPendingHotels] = useState<Set<string>>(new Set());
  const [pendingHotelGroups, setPendingHotelGroups] = useState<Set<string>>(new Set());
  const [pendingArrivalMonths, setPendingArrivalMonths] = useState<Set<number>>(new Set());
  const [pendingArrivalQuarters, setPendingArrivalQuarters] = useState<Set<number>>(new Set());
  const [pendingCreationMonths, setPendingCreationMonths] = useState<Set<number>>(new Set());
  const [pendingCreationQuarters, setPendingCreationQuarters] = useState<Set<number>>(new Set());
  const [pendingMarkets, setPendingMarkets] = useState<Set<string>>(new Set());

  // Always use the predefined STATUS_ORDER to ensure all statuses are included
  const statuses: string[] = STATUS_ORDER.filter(s => s.toLowerCase() !== 'not launched' && s.toLowerCase() !== 'not l.');

  // Add state for virtual all selection
  const [allClientsSelected, setAllClientsSelected] = useState(true);
  const [allHotelGroupsSelected, setAllHotelGroupsSelected] = useState(true);
  const [allHotelsSelected, setAllHotelsSelected] = useState(true);
  const [allMarketsSelected, setAllMarketsSelected] = useState(true);

  // Add state for Data Check modal
  const [isDataCheckModalOpen, setIsDataCheckModalOpen] = useState(false);
  
  // Add state for User Manual modal
  const [isUserManualModalOpen, setIsUserManualModalOpen] = useState(false);

  // Update selection handlers for pending filters
  const handleClientsChange = (newSelected: Set<string>) => {
    setPendingClients(newSelected);
    setAllClientsSelected(newSelected.size === 0);
  };

  const handleHotelGroupsChange = (newSelected: Set<string>) => {
    setPendingHotelGroups(newSelected);
    setAllHotelGroupsSelected(newSelected.size === 0);
  };

  const handleHotelsChange = (newSelected: Set<string>) => {
    setPendingHotels(newSelected);
    setAllHotelsSelected(newSelected.size === 0);
  };

  const handleMarketsChange = (newSelected: Set<string>) => {
    setPendingMarkets(newSelected);
    setAllMarketsSelected(newSelected.size === 0);
  };

  // Detect changes between pending and applied state
  const hasUnappliedChanges = useMemo(() => {
    const setsEqual = (a: Set<any>, b: Set<any>) => 
      a.size === b.size && [...a].every(x => b.has(x));
    
    return !setsEqual(pendingCreationYears, appliedCreationYears) ||
           !setsEqual(pendingArrivalYears, appliedArrivalYears) ||
           !setsEqual(pendingManagers, appliedManagers) ||
           !setsEqual(pendingSegments, appliedSegments) ||
           !setsEqual(pendingClients, appliedClients) ||
           !setsEqual(pendingHotels, appliedHotels) ||
           !setsEqual(pendingHotelGroups, appliedHotelGroups) ||
           !setsEqual(pendingArrivalMonths, appliedArrivalMonths) ||
           !setsEqual(pendingArrivalQuarters, appliedArrivalQuarters) ||
           !setsEqual(pendingCreationMonths, appliedCreationMonths) ||
           !setsEqual(pendingCreationQuarters, appliedCreationQuarters) ||
           !setsEqual(pendingMarkets, appliedMarkets);
  }, [
    pendingCreationYears, appliedCreationYears,
    pendingArrivalYears, appliedArrivalYears,
    pendingManagers, appliedManagers,
    pendingSegments, appliedSegments,
    pendingClients, appliedClients,
    pendingHotels, appliedHotels,
    pendingHotelGroups, appliedHotelGroups,
    pendingArrivalMonths, appliedArrivalMonths,
    pendingArrivalQuarters, appliedArrivalQuarters,
    pendingCreationMonths, appliedCreationMonths,
    pendingCreationQuarters, appliedCreationQuarters,
    pendingMarkets, appliedMarkets,
  ]);

  // Apply filters - copy pending to applied
  const handleApplyFilters = () => {
    setAppliedCreationYears(new Set(pendingCreationYears));
    setAppliedArrivalYears(new Set(pendingArrivalYears));
    setAppliedManagers(new Set(pendingManagers));
    setAppliedSegments(new Set(pendingSegments));
    setAppliedClients(new Set(pendingClients));
    setAppliedHotels(new Set(pendingHotels));
    setAppliedHotelGroups(new Set(pendingHotelGroups));
    setAppliedArrivalMonths(new Set(pendingArrivalMonths));
    setAppliedArrivalQuarters(new Set(pendingArrivalQuarters));
    setAppliedCreationMonths(new Set(pendingCreationMonths));
    setAppliedCreationQuarters(new Set(pendingCreationQuarters));
    setAppliedMarkets(new Set(pendingMarkets));
  };

  // --- NEW: Always initialize months/quarters to all values on very first render ---
  useEffect(() => {
    const allMonths = new Set([1,2,3,4,5,6,7,8,9,10,11,12]);
    const allQuarters = new Set([1,2,3,4]);
    setPendingCreationMonths(allMonths);
    setPendingCreationQuarters(allQuarters);
    setPendingArrivalMonths(allMonths);
    setPendingArrivalQuarters(allQuarters);
    setAppliedCreationMonths(allMonths);
    setAppliedCreationQuarters(allQuarters);
    setAppliedArrivalMonths(allMonths);
    setAppliedArrivalQuarters(allQuarters);
  }, []);

  // Add initialization refs for filters that depend on data
  const filtersInitialized = useRef(false);

  // Ensure all filters are initialized with all options selected after data loads (only on first load)
  useEffect(() => {
    if (filtersInitialized.current) return;
    if (availableManagers.length === 0 || availableSegments.length === 0 || 
        availableCreationYears.length === 0 || availableArrivalYears.length === 0 ||
        clientOptions.length === 0 || hotels.length === 0 || hotelGroups.length === 0 ||
        markets.length === 0) {
      return; // Wait for all data to load
    }

    // Initialize filters: All with ALL values selected
    const allManagers = new Set(availableManagers);
    const allSegments = new Set(availableSegments);
    const allCreationYears = new Set(availableCreationYears);
    const allArrivalYears = new Set(availableArrivalYears);
    const allClients = new Set(clientOptions);
    const allHotels = new Set(hotels);
    const allHotelGroups = new Set(hotelGroups);
    const allMarkets = new Set(markets);

    setPendingManagers(allManagers);
    setPendingSegments(allSegments);
    setPendingClients(allClients);
    setPendingHotels(allHotels);
    setPendingHotelGroups(allHotelGroups);
    setPendingMarkets(allMarkets);
    setPendingCreationYears(allCreationYears);
    setPendingArrivalYears(allArrivalYears);

    setAppliedManagers(allManagers);
    setAppliedSegments(allSegments);
    setAppliedClients(allClients);
    setAppliedHotels(allHotels);
    setAppliedHotelGroups(allHotelGroups);
    setAppliedMarkets(allMarkets);
    setAppliedCreationYears(allCreationYears);
    setAppliedArrivalYears(allArrivalYears);
    
    // Update "all selected" flags
    setAllClientsSelected(false); // Explicitly selected all
    setAllHotelsSelected(false); // Explicitly selected all
    setAllHotelGroupsSelected(false); // Explicitly selected all
    setAllMarketsSelected(false); // Explicitly selected all

    filtersInitialized.current = true;
  }, [availableManagers, availableSegments, clientOptions, hotels, hotelGroups, markets, availableCreationYears, availableArrivalYears]);

  // After setData(allData); in fetchData or after data is loaded
  useEffect(() => {
    if (!data || data.length === 0) return;

    // Years
    const creationYears = Array.from(new Set(data.map(row => row.request_year).filter(Boolean))).sort((a, b) => b - a);
    const arrivalYears = Array.from(new Set(data.map(row => row.arrival_year).filter(Boolean))).sort((a, b) => b - a);

    // Managers
    const managers = Array.from(new Set(data.map(row => row.request_manager).filter(Boolean))).sort();

    // Segments
    const segments = Array.from(new Set(data.map(row => row.segment).filter(Boolean))).sort();

    // Clients
    const allClients = Array.from(new Set(data.map((row: any) => row.company).filter(Boolean))).sort();

    // Hotels/Venues
    const venues = Array.from(new Set(data.map(row => row.venue).filter(Boolean))).sort();

    // Hotel Groups
    let groups = Array.from(new Set(data.map(row => row.hotel_group_name).filter(Boolean))).sort();
    const hasNoGroup = data.some(row => !row.hotel_group_name);
    if (hasNoGroup) {
      groups = [...groups, 'No Group'];
    }

    // Markets
    let marketsList = Array.from(new Set(data.map(row => row.market).filter(Boolean))).sort();
    const hasNoMarket = data.some(row => !row.market);
    if (hasNoMarket) {
      marketsList = [...marketsList, 'No Market'];
    }

    setAvailableCreationYears(creationYears);
    setAvailableArrivalYears(arrivalYears);
    setAvailableManagers(managers);
    setAvailableSegments(segments);
    setClientOptions(allClients);
    setHotels(venues);
    setHotelGroups(groups);
    setMarkets(marketsList);
  }, [data]);

  // Helper to check if a filter is in 'all' state (empty set or all options selected)
  function isAllSelected<T>(selected: Set<T>, allOptions: T[]) {
    return selected.size === 0 || selected.size === allOptions.length;
  }

  // Special helper for managers - empty set means "no filter" (show all data)
  function isManagerFilterActive(selected: Set<string>, allOptions: string[]) {
    return selected.size > 0 && selected.size < allOptions.length;
  }

  // Compute possible managers based on all other filters (cascading logic)
  
  
  // Repeat this pattern for all other possible... sets:
  // possibleHotelGroups, possibleHotels, possibleClients, possibleCreationYears, possibleCreationMonths, possibleCreationQuarters, possibleArrivalYears, possibleArrivalMonths, possibleArrivalQuarters

  
  
  
  // Date filters
  
  
  
  
  
  
  // Prune date filter sets when possible options change, but only update if changed and only if selection is non-empty and possible set is non-empty

  // Prune possible values for Managers (no auto-select, allow true multi-select)

  // Restore resetFilters function
  const resetFilters = () => {
    // Reset all filters to all values
    const allManagers = new Set(availableManagers);
    const allSegments = new Set(availableSegments);
    const allCreationYears = new Set(availableCreationYears);
    const allArrivalYears = new Set(availableArrivalYears);
    const allClients = new Set(clientOptions);
    const allHotels = new Set(hotels);
    const allHotelGroups = new Set(hotelGroups);
    const allMarkets = new Set(markets);
    const allMonths = new Set([1,2,3,4,5,6,7,8,9,10,11,12]);
    const allQuarters = new Set([1,2,3,4]);
    
    // Reset pending state
    setPendingCreationYears(allCreationYears);
    setPendingArrivalYears(allArrivalYears);
    setPendingManagers(allManagers);
    setPendingSegments(allSegments);
    setPendingClients(allClients);
    setPendingHotels(allHotels);
    setPendingHotelGroups(allHotelGroups);
    setPendingMarkets(allMarkets);
    setPendingArrivalMonths(allMonths);
    setPendingArrivalQuarters(allQuarters);
    setPendingCreationMonths(allMonths);
    setPendingCreationQuarters(allQuarters);
    
    // Also reset applied state (so data refreshes immediately)
    setAppliedCreationYears(allCreationYears);
    setAppliedArrivalYears(allArrivalYears);
    setAppliedManagers(allManagers);
    setAppliedSegments(allSegments);
    setAppliedClients(allClients);
    setAppliedHotels(allHotels);
    setAppliedHotelGroups(allHotelGroups);
    setAppliedMarkets(allMarkets);
    setAppliedArrivalMonths(allMonths);
    setAppliedArrivalQuarters(allQuarters);
    setAppliedCreationMonths(allMonths);
    setAppliedCreationQuarters(allQuarters);
    
    // Update "all selected" flags
    setAllClientsSelected(false);
    setAllHotelsSelected(false);
    setAllHotelGroupsSelected(false);
    setAllMarketsSelected(false);
  };

  // Filtered data using applied state
  const filteredData = useMemo(() => {
    return data.filter(row => {
      if (isManagerFilterActive(appliedManagers, availableManagers) && !appliedManagers.has(row.request_manager)) return false;
      if (!isAllSelected(appliedClients, clientOptions) && !appliedClients.has(row.company)) return false;
      if (!isAllSelected(appliedArrivalYears, availableArrivalYears) && !appliedArrivalYears.has(row.arrival_year)) return false;
      if (!isAllSelected(appliedCreationYears, availableCreationYears) && !appliedCreationYears.has(row.request_year)) return false;
      if (!isAllSelected(appliedSegments, availableSegments) && !appliedSegments.has(row.segment)) return false;
      const groupName = row.hotel_group_name ? row.hotel_group_name : 'No Group';
      if (!isAllSelected(appliedHotelGroups, hotelGroups) && !appliedHotelGroups.has(groupName)) return false;
      if (!isAllSelected(appliedHotels, hotels) && !appliedHotels.has(row.venue)) return false;
      if (!isAllSelected(appliedArrivalMonths, [1,2,3,4,5,6,7,8,9,10,11,12]) && !appliedArrivalMonths.has(row.arrival_month)) return false;
      if (!isAllSelected(appliedArrivalQuarters, [1,2,3,4]) && !appliedArrivalQuarters.has(row.arrival_quarter)) return false;
      if (!isAllSelected(appliedCreationMonths, [1,2,3,4,5,6,7,8,9,10,11,12]) && !appliedCreationMonths.has(row.request_month)) return false;
      if (!isAllSelected(appliedCreationQuarters, [1,2,3,4]) && !appliedCreationQuarters.has(row.request_quarter)) return false;
      const marketName = row.market ? row.market : 'No Market';
      if (!isAllSelected(appliedMarkets, markets) && !appliedMarkets.has(marketName)) return false;
      return true;
    });
  }, [data, appliedManagers, availableManagers, appliedClients, clientOptions, appliedArrivalYears, availableArrivalYears, appliedCreationYears, availableCreationYears, appliedSegments, availableSegments, appliedHotelGroups, hotelGroups, appliedHotels, hotels, appliedArrivalMonths, appliedArrivalQuarters, appliedCreationMonths, appliedCreationQuarters, appliedMarkets, markets]);

  // Group by manager + company + venue (hotel split)
  const hotelRows = useMemo(() => {
    const map = new Map();
    filteredData.forEach(row => {
      const key = `${row.request_manager}||${row.company}||${row.venue}`;
      if (!map.has(key)) {
        map.set(key, {
          request_manager: row.request_manager,
          company: row.company,
          venue: row.venue,
          requestIds: new Set(),
          statusGroups: {},
          estRevenueByStatus: {} as Record<string, number[]>,
          realRevenueByStatus: {} as Record<string, number[]>,
          estRevenueList: [],
          realRevenueList: [],
        });
        statuses.forEach(status => {
          map.get(key).statusGroups[status] = 0;
          map.get(key).estRevenueByStatus[status] = [];
          map.get(key).realRevenueByStatus[status] = [];
        });
      }
      const entry = map.get(key);
      entry.requestIds.add(row.request_id);
      if (statuses.includes(row.status)) {
        entry.statusGroups[row.status] += 1;
        if (row.estimated_revenue) entry.estRevenueByStatus[row.status].push(Number(row.estimated_revenue));
        if (row.realised_revenue) entry.realRevenueByStatus[row.status].push(Number(row.realised_revenue));
      }
      if (row.estimated_revenue) entry.estRevenueList.push(Number(row.estimated_revenue));
      if (row.realised_revenue) entry.realRevenueList.push(Number(row.realised_revenue));
    });
    // Convert arrays to counts/sums
    return Array.from(map.values()).map(row => {
      const estByStatus: Record<string, number> = {};
      const realByStatus: Record<string, number> = {};
      statuses.forEach(status => {
        estByStatus[status] = row.estRevenueByStatus[status].reduce((a: number, b: number) => a + b, 0);
        realByStatus[status] = row.realRevenueByStatus[status].reduce((a: number, b: number) => a + b, 0);
      });
      return {
        manager: row.request_manager,
        company: row.company,
        venue: row.venue,
        nbProjects: row.requestIds.size,
        ...Object.fromEntries(statuses.map(status => [status, { nb: row.statusGroups[status], est: estByStatus[status], real: realByStatus[status] }])),
        totalGroups: statuses.reduce((sum: number, status) => sum + row.statusGroups[status], 0),
        totalEst: row.estRevenueList.reduce((a: number, b: number) => a + b, 0),
        totalReal: row.realRevenueList.reduce((a: number, b: number) => a + b, 0),
      };
    });
  }, [filteredData, statuses]);

  // Group by manager + company (client row), sum hotel rows for each client
  const companyRows = useMemo(() => {
    const map = new Map();
    hotelRows.forEach(hotelRow => {
      const key = `${hotelRow.manager}||${hotelRow.company}`;
      if (!map.has(key)) {
        map.set(key, {
          manager: hotelRow.manager,
          company: hotelRow.company,
          nbProjects: 0,
          totalGroups: 0,
          totalEst: 0,
          totalReal: 0,
          statusGroups: {} as Record<string, number>,
          estByStatus: {} as Record<string, number>,
          realByStatus: {} as Record<string, number>,
        });
        statuses.forEach(status => {
          map.get(key).statusGroups[status] = 0;
          map.get(key).estByStatus[status] = 0;
          map.get(key).realByStatus[status] = 0;
        });
      }
      const entry = map.get(key);
      entry.nbProjects += hotelRow.nbProjects;
      entry.totalGroups += hotelRow.totalGroups;
      entry.totalEst += hotelRow.totalEst;
      entry.totalReal += hotelRow.totalReal;
      statuses.forEach(status => {
        entry.statusGroups[status] += (hotelRow as any)[status]?.nb || 0;
        entry.estByStatus[status] += (hotelRow as any)[status]?.est || 0;
        entry.realByStatus[status] += (hotelRow as any)[status]?.real || 0;
      });
    });
    return Array.from(map.values()).map(row => ({
      manager: row.manager,
      company: row.company,
      nbProjects: row.nbProjects,
      ...Object.fromEntries(statuses.map(status => [status, { nb: row.statusGroups[status], est: row.estByStatus[status], real: row.realByStatus[status] }])),
      totalGroups: row.totalGroups,
      totalEst: row.totalEst,
      totalReal: row.totalReal,
    }));
  }, [hotelRows, statuses]);

  // Group by manager for totals
  const managerRows = useMemo(() => {
    const map = new Map();
    filteredData.forEach(row => {
      const key = row.request_manager;
      if (!map.has(key)) {
        map.set(key, {
          manager: row.request_manager,
          requestIds: new Set(),
          statusGroups: {},
          estRevenueByStatus: {} as Record<string, number[]>,
          realRevenueByStatus: {} as Record<string, number[]>,
          estRevenueList: [],
          realRevenueList: [],
        });
        statuses.forEach(status => {
          map.get(key).statusGroups[status] = 0;
          map.get(key).estRevenueByStatus[status] = [];
          map.get(key).realRevenueByStatus[status] = [];
        });
      }
      const entry = map.get(key);
      entry.requestIds.add(row.request_id);
      if (statuses.includes(row.status)) {
        entry.statusGroups[row.status] += 1;
        if (row.estimated_revenue) entry.estRevenueByStatus[row.status].push(Number(row.estimated_revenue));
        if (row.realised_revenue) entry.realRevenueByStatus[row.status].push(Number(row.realised_revenue));
      }
      if (row.estimated_revenue) entry.estRevenueList.push(Number(row.estimated_revenue));
      if (row.realised_revenue) entry.realRevenueList.push(Number(row.realised_revenue));
    });
    // Convert arrays to counts/sums
    return Array.from(map.values()).map(row => {
      const estByStatus: Record<string, number> = {};
      const realByStatus: Record<string, number> = {};
      statuses.forEach(status => {
        estByStatus[status] = row.estRevenueByStatus[status].reduce((a: number, b: number) => a + b, 0);
        realByStatus[status] = row.realRevenueByStatus[status].reduce((a: number, b: number) => a + b, 0);
      });
      return {
        manager: row.manager,
        nbProjects: row.requestIds.size,
        ...Object.fromEntries(statuses.map(status => [status, { nb: row.statusGroups[status], est: estByStatus[status], real: realByStatus[status] }])),
        totalGroups: statuses.reduce((sum: number, status) => sum + row.statusGroups[status], 0),
        totalEst: row.estRevenueList.reduce((a: number, b: number) => a + b, 0),
        totalReal: row.realRevenueList.reduce((a: number, b: number) => a + b, 0),
      };
    });
  }, [filteredData, statuses]);

  // Table data transformer for export (level 1 only - managers)
  const getTableDataForExport = () => {
    return managerRows.map(row => {
      // Format manager name - use "Unassigned" for empty/null values
      const managerName = (typeof row.manager !== 'string' || !row.manager.trim()) ? 'Unassigned' : row.manager;
      
      const exportRow: any = {
        Manager: managerName,
        'Total Projects': row.nbProjects,
        'Groups Total': row.totalGroups
      };
      
      // Add groups columns for each status
      statuses.forEach(status => {
        const statusData = (row as any)[status] as { nb: number; est: number; real: number } | undefined;
        exportRow[`Groups ${status}`] = statusData?.nb || 0;
      });
      
      // Add estimated revenue total
      exportRow['Estimated Revenue Total'] = row.totalEst;
      
      // Add estimated revenue columns for each status
      statuses.forEach(status => {
        const statusData = (row as any)[status] as { nb: number; est: number; real: number } | undefined;
        exportRow[`Estimated Revenue ${status}`] = statusData?.est || 0;
      });
      
      // Add final revenue total and materialization
      exportRow['Final Revenue Total'] = row.totalReal;
      const bookedEst = (row as any).Booked?.est || 0;
      const materialization = bookedEst > 0 ? (row.totalReal / bookedEst) * 100 : 0;
      exportRow['Materialization %'] = materialization;
      
      return exportRow;
    });
  };

  // Update the grid style calculation
  const gridStyle = useMemo(() => {
    return {
      display: 'grid',
      gridTemplateColumns: [
        managerColumnWidth,
        nbProjectsWidth,
        ...Array(statuses.length + 1).fill(groupsColumnsWidth), // GROUPS
        ...Array(statuses.length + 1).fill(otherColumnsWidth), // ESTIMATED REVENUE
        realisedRevenueWidth,
        materializationWidth
      ].join(' '),
      alignItems: 'center'
    };
  }, [statuses]);

  // Sort so "Unassigned" (or null/empty) manager is last
  const sortedRows = [...managerRows].sort((a, b) => {
    const isAUnassigned = !a.manager || a.manager === "Unassigned";
    const isBUnassigned = !b.manager || b.manager === "Unassigned";
    if (isAUnassigned && !isBUnassigned) return 1;
    if (!isAUnassigned && isBUnassigned) return -1;
    return (a.manager || "").localeCompare(b.manager || "");
  });

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-[90rem] mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="page-title">Estimated Revenue per Manager</h1>
          <div className="flex items-center space-x-4">
            <ExportButton
              reportName="Estimated Revenue per Manager"
              data={data}
              filters={{
                selectedCreationYears: appliedCreationYears,
                selectedArrivalYears: appliedArrivalYears,
                selectedManagers: appliedManagers,
                selectedSegments: appliedSegments,
                selectedClients: appliedClients,
                selectedHotels: appliedHotels,
                selectedHotelGroups: appliedHotelGroups,
                selectedArrivalMonths: appliedArrivalMonths,
                selectedArrivalQuarters: appliedArrivalQuarters,
                selectedCreationMonths: appliedCreationMonths,
                selectedCreationQuarters: appliedCreationQuarters,
                allClientsSelected,
                allHotelsSelected,
                allHotelGroupsSelected
              }}
              tableDataTransformer={getTableDataForExport}
            />
            <CacheStatus />
          </div>
        </div>
        
        <FilterPanel
          selectedCreationYears={pendingCreationYears}
          selectedArrivalYears={pendingArrivalYears}
          selectedManagers={pendingManagers}
          selectedSegments={pendingSegments}
          availableCreationYears={availableCreationYears}
          availableArrivalYears={availableArrivalYears}
          availableManagers={availableManagers}
          availableSegments={availableSegments}
          setSelectedCreationYears={setPendingCreationYears}
          setSelectedArrivalYears={setPendingArrivalYears}
          setSelectedManagers={setPendingManagers}
          setSelectedSegments={setPendingSegments}
          resetFilters={resetFilters}
          selectedArrivalMonths={pendingArrivalMonths}
          selectedArrivalQuarters={pendingArrivalQuarters}
          setSelectedArrivalMonths={setPendingArrivalMonths}
          setSelectedArrivalQuarters={setPendingArrivalQuarters}
          selectedCreationMonths={pendingCreationMonths}
          selectedCreationQuarters={pendingCreationQuarters}
          setSelectedCreationMonths={setPendingCreationMonths}
          setSelectedCreationQuarters={setPendingCreationQuarters}
          clients={clientOptions}
          selectedClients={pendingClients}
          onClientsChange={handleClientsChange}
          allClientsSelected={allClientsSelected}
          hotels={hotels}
          selectedHotels={pendingHotels}
          onHotelsChange={handleHotelsChange}
          allHotelsSelected={allHotelsSelected}
          hotelGroups={hotelGroups}
          selectedHotelGroups={pendingHotelGroups}
          onHotelGroupsChange={handleHotelGroupsChange}
          allHotelGroupsSelected={allHotelGroupsSelected}
          onApplyFilters={handleApplyFilters}
          hasUnappliedChanges={hasUnappliedChanges}
          selectedMarkets={pendingMarkets}
          markets={markets}
          allMarketsSelected={allMarketsSelected}
          onMarketsChange={handleMarketsChange}
        />

        {loadingProgress && (
          <div className="mb-4 bg-blue-50 text-blue-700 p-2 rounded-md text-center">
            {loadingProgress}
          </div>
        )}

        <RevenueGraph data={filteredData} />

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <RevenueTable
            isLoading={isLoading}
            error={error || undefined}
            sortedRows={sortedRows}
            statuses={statuses}
            gridStyle={gridStyle}
            expandedManagers={expandedManagers}
            setExpandedManagers={setExpandedManagers}
            hasCompanySplit={manager => companyRows.some(row => row.manager === manager)}
            getCompanySplit={manager => companyRows.filter(row => row.manager === manager)}
            hasHotelSplit={(manager, company) => hotelRows.some(row => row.manager === manager && row.company === company)}
            getHotelSplit={(manager, company) => hotelRows.filter(row => row.manager === manager && row.company === company)}
          />
        </div>

        {/* Add the total row if there are 2 or more visible rows */}
        {managerRows.length >= 2 && (
          <TotalRow
            rows={managerRows}
            statuses={statuses}
            gridStyle={gridStyle}
          />
        )}

        {/* Data Check link */}
        <div className="mt-4 text-center">
          <button
            onClick={() => setIsDataCheckModalOpen(true)}
            className="text-blue-600 hover:text-blue-800 text-sm underline"
          >
            Data Check
          </button>
        </div>

        {/* Data Check Modal */}
        <DataCheckModal
          isOpen={isDataCheckModalOpen}
          onClose={() => setIsDataCheckModalOpen(false)}
          filters={{
            selectedCreationYears: appliedCreationYears,
            selectedArrivalYears: appliedArrivalYears,
            selectedManagers: appliedManagers,
            selectedSegments: appliedSegments,
            selectedClients: appliedClients,
            selectedHotels: appliedHotels,
            selectedHotelGroups: appliedHotelGroups,
            selectedArrivalMonths: appliedArrivalMonths,
            selectedArrivalQuarters: appliedArrivalQuarters,
            selectedCreationMonths: appliedCreationMonths,
            selectedCreationQuarters: appliedCreationQuarters,
            allClientsSelected,
            allHotelsSelected,
            allHotelGroupsSelected
          }}
          availableOptions={{
            availableCreationYears,
            availableArrivalYears,
            availableManagers,
            availableSegments,
            clients: clientOptions,
            hotels,
            hotelGroups
          }}
        />

        {/* User Manual Modal */}
        <UserManualModal
          isOpen={isUserManualModalOpen}
          onClose={() => setIsUserManualModalOpen(false)}
        />
      </main>
    </div>
  );
}